import { generatePath } from 'react-router-dom';
import { PRIVATE_ROUTES } from './routes';

export function settingsChannelProviderPath(channelSlug: string, providerSlug: string): string {
    return generatePath(PRIVATE_ROUTES.SETTINGS_CHANNEL_PROVIDER, {
        channelType: channelSlug,
        provider: providerSlug,
    });
}

export function settingsChannelDetailPath(channelSlug: string): string {
    return generatePath(PRIVATE_ROUTES.SETTINGS_CHANNEL_DETAIL, {
        channelType: channelSlug,
    });
}

/**
 * Deep link to the create-campaign wizard seeded from a saved template.
 *
 * Two shapes, one helper, because "Use in campaign" is one action offered from
 * both template lists and the params must not drift apart again:
 *   - EMAIL carries `channel=EMAIL` as well, because the wizard has to be
 *     switched off its SMS default AND into DESIGNED mode before the fetch lands.
 *   - SMS carries the id alone — SMS is already the default channel, and
 *     `?channel=SMS` has always been a no-op the wizard ignores.
 *
 * Both are resolved in `resolveInitialWizardState` / the wizard's mount effect
 * (`use-create-campaign-form.ts`), which fetches the template and seeds the body.
 */
export function campaignFromSmsTemplatePath(smsTemplateId: string): string {
    return `${PRIVATE_ROUTES.CREATE_CAMPAIGN}?smsTemplateId=${encodeURIComponent(smsTemplateId)}`;
}

/** Email counterpart of {@link campaignFromSmsTemplatePath}. */
export function campaignFromEmailTemplatePath(emailTemplateId: string): string {
    return `${PRIVATE_ROUTES.CREATE_CAMPAIGN}?channel=EMAIL&emailTemplateId=${encodeURIComponent(
        emailTemplateId,
    )}`;
}

/**
 * Deep link to one unified conversation.
 *
 * `InboxPage` treats the URL as the source of truth for which conversation is
 * active (it syncs `routeId → setActiveUnifiedConversationId`), so navigating
 * here is the whole handoff — callers outside `features/inbox/` must not reach
 * for the inbox's Zustand store, which is deliberately not part of its public
 * surface.
 */
export function inboxConversationPath(
    unifiedConversationId: string,
    /**
     * Which channel tab to open the thread on. Pass it whenever the caller
     * KNOWS — a compose that just sent an email lands on Email, one that sent an
     * SMS lands on SMS. Omitted, the thread falls back to its own heuristic
     * (`pickDefaultChannel`: highest unread, ties by recency), which is right
     * when nobody has an opinion and wrong right after a send: one unread SMS
     * outranks the email the user is being shown for.
     */
    channel?: InboxThreadChannel,
): string {
    const path = generatePath(PRIVATE_ROUTES.INBOX_DETAIL, { unifiedConversationId });
    return channel === undefined
        ? path
        : `${path}?${INBOX_THREAD_CHANNEL_PARAM}=${encodeURIComponent(channel)}`;
}

/**
 * The channel tabs a thread can be opened on. Mirrors `MessageChannel`.
 *
 * A runtime map rather than a bare union so every caller NAMES the tab
 * (`INBOX_THREAD_CHANNEL.SMS`) instead of re-typing the literal at each
 * `inboxConversationPath()` call site and in `ThreadPane`'s `?thread=` guard —
 * the same shape as `SYSTEM_ROLE_NAME` / `PERMISSION` in
 * `features/users-roles/constants`.
 */
export const INBOX_THREAD_CHANNEL = {
    SMS: 'SMS',
    EMAIL: 'EMAIL',
    WHATSAPP: 'WHATSAPP',
} as const;

export type InboxThreadChannel = (typeof INBOX_THREAD_CHANNEL)[keyof typeof INBOX_THREAD_CHANNEL];

/**
 * Query param naming the channel tab a thread should open on.
 *
 * Deliberately NOT `channel`, for the same reason the compose params below are
 * not: `/inbox?channel=SMS` already means "filter the conversation list", and
 * one name cannot mean two things. `ThreadPane` consumes it on arrival and
 * strips it, so a refresh or a Back does not re-pin a tab the user has since
 * changed.
 */
export const INBOX_THREAD_CHANNEL_PARAM = 'thread';

/**
 * Query param naming the internal note a mention alert is about.
 *
 * The producer is the BACKEND — `notification-trigger.worker.ts` builds
 * `/inbox/${unifiedConversationId}?note=${noteId}` for a
 * `CONVERSATION_NOTE_MENTION` notification — so it cannot import this constant.
 * That makes the name a two-sided contract held together by a string literal on
 * each end, the exact shape the compose params below are tested for; the
 * backend pins its half in `notification-trigger.worker.spec.ts`, and
 * `route-helpers.test.ts` pins this one.
 *
 * `InboxPage` opens the Notes tab, flags the note to flash, and strips the
 * param so a re-render or a Back does not re-fire it.
 */
export const INBOX_NOTE_PARAM = 'note';

/**
 * Deep link to one unified conversation with a specific internal note flagged
 * to scroll into view and flash — the mention alert's destination.
 */
export function inboxNotePath(unifiedConversationId: string, noteId: string): string {
    const path = generatePath(PRIVATE_ROUTES.INBOX_DETAIL, { unifiedConversationId });
    return `${path}?${INBOX_NOTE_PARAM}=${encodeURIComponent(noteId)}`;
}

/**
 * The email builder for one template, optionally remembering where to go back
 * to. Both entry points into the builder — the create dialog and the Library's
 * "Edit template" — go through here rather than assembling the path inline, so
 * the `returnTo` encoding lives in one place.
 */
export function emailTemplateBuilderPath(id: string, returnTo?: string): string {
    const path = generatePath(PRIVATE_ROUTES.EMAIL_TEMPLATE_BUILDER, { id });
    return returnTo ? `${path}?returnTo=${encodeURIComponent(returnTo)}` : path;
}

/**
 * Query param that opens the Inbox's "compose new SMS" takeover with an SMS
 * template already loaded into the body.
 *
 * Deliberately NOT `channel` — `/inbox?channel=SMS` already means "filter the
 * conversation list to SMS" (`useInboxFilters`), so reusing it would make one
 * link mean two things. `InboxPage` reads this once on mount and strips it,
 * because `useInboxFilters.writeToUrl` rebuilds the query string from filter
 * keys alone and would drop it the moment the user touches a filter.
 */
export const INBOX_COMPOSE_SMS_TEMPLATE_PARAM = 'smsTemplateId';

/**
 * Deep link to a fresh SMS compose in the Inbox, seeded with one template.
 *
 * Carries the template *id*, not its body: a body is up to 1600 characters and
 * has no business in a URL, and the id is what every other template hand-off in
 * the app passes (`?emailTemplateId=`, `?templateId=`). The composer resolves
 * it, so a link shared or re-opened later picks up the template's current text
 * rather than a stale copy.
 */
export function inboxComposeSmsPath(smsTemplateId: string): string {
    return `${PRIVATE_ROUTES.INBOX}?${INBOX_COMPOSE_SMS_TEMPLATE_PARAM}=${encodeURIComponent(
        smsTemplateId,
    )}`;
}

/**
 * Email counterpart of {@link INBOX_COMPOSE_SMS_TEMPLATE_PARAM}. Same reasoning
 * applies — it is not `channel`, and `InboxPage` strips it on mount.
 *
 * The name matches the param the campaign wizard already accepts
 * (`/campaigns/new?emailTemplateId=`), so one id means the same thing wherever
 * a template is handed off.
 */
export const INBOX_COMPOSE_EMAIL_TEMPLATE_PARAM = 'emailTemplateId';

/**
 * Deep link to a fresh email compose in the Inbox, seeded with one template.
 *
 * Carries the id rather than the compiled HTML for the same reason the SMS
 * helper carries an id rather than a body: the composer resolves it, so the
 * link picks up the template's current design instead of a stale snapshot.
 */
export function inboxComposeEmailPath(emailTemplateId: string): string {
    return `${PRIVATE_ROUTES.INBOX}?${INBOX_COMPOSE_EMAIL_TEMPLATE_PARAM}=${encodeURIComponent(
        emailTemplateId,
    )}`;
}

/**
 * Third compose param, for a template-library (marketplace) SMS entry.
 *
 * Those entries are catalog copy, not `SmsTemplate` rows — `sms.catalog.ts`
 * inlines the body straight into the definition — so there is no template id to
 * hand {@link inboxComposeSmsPath}. Hence a second SMS param rather than an
 * overload of the first: the two resolve against different endpoints, and one
 * param that sometimes means a UUID and sometimes a slug is the ambiguity the
 * comments above exist to avoid. `InboxPage` strips it on mount like the others.
 */
export const INBOX_COMPOSE_SMS_LIBRARY_PARAM = 'smsLibrarySlug';

/**
 * Deep link to a fresh SMS compose in the Inbox, seeded from a template-library
 * entry's catalog body.
 *
 * The slug IS the id here, so this keeps the rule the two helpers above follow —
 * carry an identifier, never the body itself. The composer resolves it through
 * the library detail endpoint (`previewMeta.body`), which is already in React
 * Query's cache from the preview drawer the user clicked through.
 */
export function inboxComposeSmsLibraryPath(librarySlug: string): string {
    return `${PRIVATE_ROUTES.INBOX}?${INBOX_COMPOSE_SMS_LIBRARY_PARAM}=${encodeURIComponent(
        librarySlug,
    )}`;
}

/**
 * WhatsApp counterpart of {@link INBOX_COMPOSE_SMS_TEMPLATE_PARAM}. Same
 * reasoning applies — it is not `channel`, and `InboxPage` strips it on mount.
 *
 * The name matches the field the compose DTO already uses
 * (`ComposeWhatsAppDto.whatsappTemplateId`), so one id means the same thing
 * wherever a template is handed off.
 */
export const INBOX_COMPOSE_WHATSAPP_TEMPLATE_PARAM = 'whatsappTemplateId';

/**
 * Deep link to a fresh WhatsApp compose in the Inbox, seeded with one template.
 *
 * Carries the id for the same reason the SMS and email helpers do. Unlike those
 * two the panel needs no lookup of its own: `WhatsAppTemplateSelect` resolves
 * the id, so landing here cannot race a slow fetch against the user's typing.
 *
 * Callers must pass an APPROVED template. Meta rejects anything else with
 * 98008, and the picker this seeds lists APPROVED only — an unapproved id would
 * select nothing and leave the composer looking broken.
 */
export function inboxComposeWhatsAppPath(whatsappTemplateId: string): string {
    return `${PRIVATE_ROUTES.INBOX}?${INBOX_COMPOSE_WHATSAPP_TEMPLATE_PARAM}=${encodeURIComponent(
        whatsappTemplateId,
    )}`;
}

/**
 * Warm the ticket detail route's code-split chunk before navigating to it.
 *
 * Route elements are `React.lazy` and the app wires up no Suspense fallback, so
 * `RouterProvider`'s internal boundary renders `null` while a cold chunk
 * downloads — the content area goes blank. Calling this on intent (a dialog
 * opening, a row hover) removes that frame.
 *
 * `routes.tsx` builds its `lazy()` from this same function so the import
 * specifier has one home and the two can never drift onto different chunks.
 * The dynamic import lives inside the function body, so nothing here statically
 * depends on the page — no cycle with `routes.tsx`.
 */
export const preloadTicketDetailPage = (): Promise<
    typeof import('@/pages/tickets/ticket-detail-page')
> => import('@/pages/tickets/ticket-detail-page');

/** Same contract as {@link preloadTicketDetailPage} for the opportunity detail route. */
export const preloadOpportunityDetailPage = (): Promise<
    typeof import('@/pages/opportunities/opportunity-detail-page')
> => import('@/pages/opportunities/opportunity-detail-page');

/** Query param that pre-selects a module on the Module Setup page (read by `ModuleSetupView`). */
export const MODULE_SETUP_MODULE_PARAM = 'module';
/** Query param naming the field Module Setup scrolls to and highlights. */
export const MODULE_SETUP_FIELD_PARAM = 'field';

/**
 * Deep link to Module Setup with one module selected, by its slug (e.g.
 * `'opportunities'`), optionally scrolled to one of its fields.
 */
export function moduleSetupPath(moduleSlug: string, options: { fieldId?: string } = {}): string {
    const query = new URLSearchParams({ [MODULE_SETUP_MODULE_PARAM]: moduleSlug });
    if (options.fieldId) query.set(MODULE_SETUP_FIELD_PARAM, options.fieldId);
    return `${PRIVATE_ROUTES.SETTINGS_MODULE_SETUP}?${query.toString()}`;
}
